import * as React from "react";
import { X } from "lucide-react";
import { Badge } from "../ui/badge";
import {
  Command,
  CommandGroup,
  CommandItem,
  CommandList,
} from "../ui/command";
import {
  FormControl,
  FormError,
  FormField,
  FormLabel,
} from "./form";
import {
  Popover,
  PopoverAnchor,
  PopoverContent,
} from "../ui/popover";
import { Command as CommandPrimitive } from "cmdk";
import type { ChoicesProps, InputProps } from "ra-core";
import {
  useChoices,
  useChoicesContext,
  useGetRecordRepresentation,
  useInput,
  useTranslate,
  FieldTitle,
  useEvent,
} from "ra-core";
import { InputHelperText } from "./input-helper-text";
import { useCallback } from "react";
import { PopoverProps } from "@radix-ui/react-popover";

/**
 * Form control that lets users choose multiple values from a list using a dropdown with autocompletion.
 *
 * This input allows editing array values with a searchable dropdown interface and displays selected items as removable badges.
 * Works seamlessly inside ReferenceArrayInput for editing many-to-many relationships.
 *
 * @see {@link https://marmelab.com/shadcn-admin-kit/docs/autocompletearrayinput/ AutocompleteArrayInput documentation}
 *
 * @example
 * import {
 *   Create,
 *   SimpleForm,
 *   AutocompleteArrayInput,
 *   ReferenceArrayInput,
 * } from '.';
 *
 * const PostCreate = () => (
 *   <Create>
 *     <SimpleForm>
 *       <AutocompleteArrayInput
 *         source="tags"
 *         choices={[
 *           { id: 'tech', name: 'Tech' },
 *           { id: 'news', name: 'News' },
 *           { id: 'lifestyle', name: 'Lifestyle' },
 *         ]}
 *       />
 *       <ReferenceArrayInput source="tag_ids" reference="tags">
 *         <AutocompleteArrayInput />
 *       </ReferenceArrayInput>
 *     </SimpleForm>
 *   </Create>
 * );
 */
export const AutocompleteArrayInput = (
  props: Omit<InputProps, "source"> &
    Partial<Pick<InputProps, "source">> &
    ChoicesProps & {
      className?: string;
      disableValue?: string;
      filterToQuery?: (searchText: string) => any;
      translateChoice?: boolean;
      placeholder?: string;
      inputText?:
        | React.ReactNode
        | ((option: any | undefined) => React.ReactNode);
    } & Pick<PopoverProps, "modal">,
) => {
  const { filterToQuery = DefaultFilterToQuery, inputText, modal } = props;
  const {
    allChoices = [],
    source,
    resource,
    isFromReference,
    setFilters,
  } = useChoicesContext(props);
  const { id, field, isRequired } = useInput({ ...props, source });
  const translate = useTranslate();
  const { placeholder = translate("ra.action.search", { _: "Search..." }) } =
    props;

  const getRecordRepresentation = useGetRecordRepresentation(resource);
  const { getChoiceText, getChoiceValue } = useChoices({
    optionText:
      props.optionText ?? (isFromReference ? getRecordRepresentation : "name"),
    optionValue: props.optionValue ?? "id",
    disableValue: props.disableValue,
    translateChoice: props.translateChoice ?? !isFromReference,
  });

  const inputRef = React.useRef<HTMLInputElement>(null);
  const listRef = React.useRef<HTMLDivElement>(null);
  const [open, setOpen] = React.useState(false);

  // The live API can return `""` (not `null`/`[]`) for an unset array
  // column (HATCH-501 review: `TypeError: E.map is not a function` from the
  // same quirk hitting `TextArrayInput` — `field.value.filter`/`.includes`
  // below would crash the same way on a non-array value).
  const fieldValue: any[] = Array.isArray(field.value) ? field.value : [];

  const handleUnselect = useEvent((choice: any) => {
    field.onChange(
      fieldValue.filter((v: any) => v !== getChoiceValue(choice)),
    );
  });

  const handleKeyDown = useEvent((e: React.KeyboardEvent<HTMLDivElement>) => {
    const input = inputRef.current;
    if (input) {
      if (e.key === "Delete" || e.key === "Backspace") {
        if (input.value === "") {
          field.onChange(fieldValue.slice(0, -1));
        }
      }
      // This is not a default behavior of the <input /> field
      if (e.key === "Escape") {
        input.blur();
      }
    }
  });

  const availableChoices = allChoices.filter(
    (choice) => !fieldValue.includes(getChoiceValue(choice)),
  );
  const selectedChoices = allChoices.filter((choice) =>
    fieldValue.includes(getChoiceValue(choice)),
  );
  const [filterValue, setFilterValue] = React.useState("");

  const getInputText = useCallback(
    (selectedChoice: any) => {
      if (typeof inputText === "function") {
        return inputText(selectedChoice);
      }
      if (inputText !== undefined) {
        return inputText;
      }
      return getChoiceText(selectedChoice);
    },
    [inputText, getChoiceText],
  );

  return (
    <FormField className={props.className} id={id} name={field.name}>
      {props.label !== false && (
        <FormLabel>
          <FieldTitle
            label={props.label}
            source={props.source ?? source}
            resource={resource}
            isRequired={isRequired}
          />
        </FormLabel>
      )}
      <FormControl>
        {/*
         * The dropdown lives in a Radix `PopoverContent` (portaled to
         * `document.body`) rather than an in-flow `absolute` child of this
         * component, like the plain-scalar `AutocompleteInput`'s combobox
         * already does — an in-flow dropdown gets clipped by any ancestor
         * with non-`visible` overflow, which `EditableTable`'s row-scrolling
         * table wrapper always has (Notion feedback on HATCH-128: "dropdown
         * of categories is being clipped by table boundaries").
         * `onOpenAutoFocus`/`onPointerDownOutside`/etc. are all suppressed
         * since open/close here stays driven by the `CommandPrimitive.Input`'s
         * own focus/blur (`setOpen` below), not Radix's own dismiss logic —
         * letting Radix's outside-click handling fire too would close the
         * popover on a click back into the input box itself, since that box
         * lives outside the portaled content. `modal`, when set by a caller
         * embedding this inside a modal `Dialog`/`Sheet`, keeps this popover
         * in `react-remove-scroll`'s lock stack instead of having the
         * dialog's own lock swallow wheel events over it (same shape
         * `AutocompleteInput`'s own `modal` prop already has; see
         * `EditableTable.tsx`'s `FieldInput`, HATCH-378).
         */}
        <Popover open={open && availableChoices.length > 0} modal={modal}>
          <Command
            onKeyDown={handleKeyDown}
            shouldFilter={!isFromReference}
            className="overflow-visible bg-transparent"
          >
            <PopoverAnchor asChild>
              <div className="group rounded-md bg-transparent dark:bg-input/30 border border-input px-3 py-1.75 text-sm transition-all ring-offset-background focus-within:border-ring focus-within:ring-ring/50 focus-within:ring-[3px]">
                <div className="flex flex-wrap gap-1">
                  {selectedChoices.map((choice) => (
                    <Badge key={getChoiceValue(choice)} variant="outline">
                      {getInputText(choice)}
                      <button
                        className="ml-1 rounded-full outline-none ring-offset-background focus:ring-2 focus:ring-ring focus:ring-offset-2"
                        onKeyDown={(e) => {
                          if (e.key === "Enter") {
                            handleUnselect(choice);
                          }
                        }}
                        onMouseDown={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                        }}
                        onClick={(e) => {
                          e.preventDefault();
                          handleUnselect(choice);
                        }}
                      >
                        <span className="sr-only">
                          {translate("ra.action.remove", {
                            _: "Remove",
                          })}
                        </span>
                        <X className="h-3 w-3" />
                      </button>
                    </Badge>
                  ))}
                  {/* Avoid having the "Search" Icon by not using CommandInput */}
                  <CommandPrimitive.Input
                    ref={inputRef}
                    value={filterValue}
                    onValueChange={(filter) => {
                      setFilterValue(filter);
                      requestAnimationFrame(() => {
                        listRef.current?.scrollTo(0, 0);
                      });
                      // We don't want the ChoicesContext to filter the choices if the input
                      // is not from a reference as it would also filter out the selected values
                      if (isFromReference) {
                        setFilters(filterToQuery(filter), undefined, true);
                      }
                    }}
                    onBlur={() => setOpen(false)}
                    onFocus={() => setOpen(true)}
                    placeholder={placeholder}
                    className="ml-2 flex-1 bg-transparent outline-none placeholder:text-muted-foreground"
                  />
                </div>
              </div>
            </PopoverAnchor>
            <PopoverContent
              align="start"
              className="w-full max-w-(--radix-popover-trigger-width) p-0"
              onOpenAutoFocus={(e) => e.preventDefault()}
              onCloseAutoFocus={(e) => e.preventDefault()}
              onPointerDownOutside={(e) => e.preventDefault()}
              onInteractOutside={(e) => e.preventDefault()}
            >
              <CommandList
                ref={listRef}
                className="max-h-(--radix-popover-content-available-height)"
              >
                <CommandGroup className="h-full overflow-auto">
                  {availableChoices.map((choice) => {
                    const choiceText = getChoiceText(choice);
                    return (
                      <CommandItem
                        key={getChoiceValue(choice)}
                        value={getChoiceValue(choice)}
                        keywords={
                          React.isValidElement(choiceText)
                            ? undefined
                            : [choiceText]
                        }
                        onMouseDown={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                        }}
                        onSelect={() => {
                          setFilterValue("");
                          if (isFromReference) {
                            setFilters(filterToQuery(""));
                          }
                          field.onChange([
                            ...fieldValue,
                            getChoiceValue(choice),
                          ]);
                        }}
                        className="cursor-pointer"
                      >
                        {choiceText}
                      </CommandItem>
                    );
                  })}
                </CommandGroup>
              </CommandList>
            </PopoverContent>
          </Command>
        </Popover>
      </FormControl>
      <InputHelperText helperText={props.helperText} />
      <FormError />
    </FormField>
  );
};

const DefaultFilterToQuery = (searchText: string) => ({ q: searchText });
